import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, X } from "lucide-react";
import { api } from "../../lib/api";
import { EmptyState, Skeleton } from "../../components/common";

/**
 * CampaignGroupsPanel — internal-only campaign grouping tool.
 *
 * Groups are shown ONLY to admins; nothing about them ever ships to the brand
 * portal. The admin uses them as buckets to bulk-edit fields across many
 * campaigns without opening each one individually.
 *
 * Rendered as a sub-tab inside Admin → Campaigns (a "Groups" mode).
 */
export function CampaignGroupsPanel({ campaigns, onRequestBulkEdit }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // { id?, name, description, color }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaign-groups");
      setGroups(data.groups || []);
    } catch { toast.error("Failed to load groups"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => setEditing({ name: "", description: "", color: "#e8613a" });
  const openEdit = (g) => setEditing({ ...g });

  const save = async () => {
    if (!editing?.name?.trim()) return toast.error("Group name is required");
    setBusy(true);
    try {
      const body = { name: editing.name.trim(), description: editing.description || null, color: editing.color || null };
      if (editing.id) await api.put(`/campaign-groups/${editing.id}`, body);
      else await api.post("/campaign-groups", body);
      toast.success(editing.id ? "Group updated" : "Group created");
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (g) => {
    const msg = g.campaign_count > 0
      ? `"${g.name}" has ${g.campaign_count} campaign${g.campaign_count === 1 ? "" : "s"}. Deleting will unassign them (no campaigns will be deleted). Continue?`
      : `Delete group "${g.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/campaign-groups/${g.id}`);
      toast.success("Group deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const membersOf = (groupId) => campaigns.filter((c) => c.group_id === groupId);

  return (
    <div data-testid="campaign-groups-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-xl font-semibold tracking-tight">Groups</h2>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Internal-only buckets for bulk-editing campaigns. Never visible to brand users.</p>
        </div>
        <button
          onClick={openCreate}
          className="wl-btn-primary inline-flex items-center gap-2"
          data-testid="create-group-btn"
        ><Plus size={16} /> Create Group</button>
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}</div>
      ) : groups.length === 0 ? (
        <EmptyState
          title="No groups yet"
          subtitle="Create a group (e.g. 'Q1 2026' or 'Beauty priority') to organise campaigns for bulk edits."
        />
      ) : (
        <div className="space-y-3">
          {groups.map((g) => {
            const members = membersOf(g.id);
            return (
              <div
                key={g.id}
                className="wl-card-solid p-4 flex items-center gap-4"
                data-testid={`group-row-${g.id}`}
              >
                <div className="w-3 h-full min-h-[3rem] rounded-full shrink-0" style={{ background: g.color || "var(--brand)" }} aria-hidden />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate" data-testid={`group-name-${g.id}`}>{g.name}</div>
                  {g.description && <div className="text-xs text-[var(--text-secondary)] mt-0.5 truncate">{g.description}</div>}
                  <div className="text-[11px] text-[var(--text-secondary)] mt-1">
                    {members.length} campaign{members.length === 1 ? "" : "s"}
                    {members.length > 0 && (
                      <>
                        {" · "}
                        <span className="italic">{members.slice(0, 3).map((c) => c.campaign_name || c.campaign_id).join(", ")}{members.length > 3 ? `, +${members.length - 3} more` : ""}</span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => onRequestBulkEdit(members.map((c) => c.id), `group "${g.name}"`)}
                  disabled={members.length === 0}
                  className="wl-btn-secondary text-sm disabled:opacity-40"
                  data-testid={`group-bulk-edit-${g.id}`}
                  title={members.length === 0 ? "No campaigns in this group yet" : "Bulk-edit every campaign in this group"}
                >Bulk edit members</button>
                <button
                  onClick={() => openEdit(g)}
                  className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] p-1.5"
                  aria-label="Edit group"
                  data-testid={`group-edit-${g.id}`}
                ><Pencil size={15} /></button>
                <button
                  onClick={() => remove(g)}
                  className="text-[var(--text-secondary)] hover:text-red-600 p-1.5"
                  aria-label="Delete group"
                  data-testid={`group-delete-${g.id}`}
                ><Trash2 size={15} /></button>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !busy && setEditing(null)}>
          <div className="wl-card-solid max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="group-edit-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold">{editing.id ? "Edit group" : "Create group"}</h3>
              <button onClick={() => setEditing(null)} className="text-[var(--text-secondary)]" aria-label="Close"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="wl-label">Name</label>
                <input
                  className="wl-input"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Q1 2026"
                  autoFocus
                  data-testid="group-name-input"
                />
              </div>
              <div>
                <label className="wl-label">Description (optional)</label>
                <textarea
                  className="wl-input"
                  rows={2}
                  value={editing.description || ""}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  data-testid="group-description-input"
                />
              </div>
              <div>
                <label className="wl-label">Colour</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={editing.color || "#e8613a"}
                    onChange={(e) => setEditing({ ...editing, color: e.target.value })}
                    className="w-10 h-10 rounded border border-[var(--border-default)] bg-transparent"
                    data-testid="group-color-input"
                  />
                  <span className="text-xs text-[var(--text-secondary)] font-mono">{editing.color || "#e8613a"}</span>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditing(null)} className="wl-btn-secondary" disabled={busy}>Cancel</button>
              <button onClick={save} className="wl-btn-primary" disabled={busy} data-testid="group-save-btn">{busy ? "Saving…" : (editing.id ? "Save" : "Create")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
